/**
 * Cross-incident resource contention — dashboard-level, not per-incident.
 *
 * A brigade/station committed to one job isn't available for another; this
 * app has no live CAD to enforce that, so the best it can do is flag it. Only
 * `ResourceUnit`s with a `brigadeStationId` (see `resources.ts`) are eligible
 * — that id is the one reliable join key across incidents. Units without it
 * (non-RFS agencies today, or pre-Phase-1 legacy entries) are excluded from
 * detection rather than fuzzy-matched on the free-text `brigade` string.
 */
import type { Incident } from './incident.js';
import { isCommitted } from './resources.js';

export interface ContentionFlag {
  stationId: string;
  stationName: string;
  incidentIds: string[];
}

/**
 * Groups committed units by `brigadeStationId` across all provided
 * incidents; any station id committed on more than one incident is a flag.
 * Pure — no I/O, no mutation of the incidents passed in.
 */
export function computeContention(incidents: readonly Incident[]): ContentionFlag[] {
  const byStation = new Map<string, { stationName: string; incidentIds: Set<string> }>();

  for (const incident of incidents) {
    for (const unit of incident.resources) {
      if (!unit.brigadeStationId || !isCommitted(unit.status)) continue;
      let entry = byStation.get(unit.brigadeStationId);
      if (!entry) {
        entry = { stationName: unit.brigade ?? unit.callsign, incidentIds: new Set() };
        byStation.set(unit.brigadeStationId, entry);
      }
      entry.incidentIds.add(incident.id);
    }
  }

  const flags: ContentionFlag[] = [];
  for (const [stationId, entry] of byStation) {
    if (entry.incidentIds.size > 1) {
      flags.push({ stationId, stationName: entry.stationName, incidentIds: [...entry.incidentIds] });
    }
  }
  return flags;
}
